import { PcbNodeTypes, } from "@buildwithflux/models";
import { humanizeLengthValue } from "@buildwithflux/shared";
import { calcKeepoutForNodeAndType } from "../../fills/keepOutHelpers";
import { PcbTreeManager } from "../layout_engine/PcbTreeManager";
import { PcbLayoutRuleCompiler } from "../layout_engine/rule_parsing_and_compilation/PcbLayoutRuleCompiler";
import { PcbLayoutRuntimeRulesManager } from "../layout_engine/rule_parsing_and_compilation/PcbLayoutRuntimeRulesManager";
import { getExpectedNewRuleValue, migrationParseRules, wipeLocalRulesFromNodeType } from "./ruleMigrations";
/*
    When we shipped polygon, we also decided that we wanted to change how the keepOut rule works, in the context of board edge spacing.
    Previously setting a keepout on a net or a layout would cause the fill to stay away from the edge of the layout.
    Now, we have a specific rule for that, called boardInsetMargin.

    This migration creates a new boardInsetMargin when possible, wiping out old keepout rules on nets.
*/
const keepoutRuleKeys = ["keepOut", "minKeepOut", "maxKeepOut", "keepIn", "minKeepIn", "maxKeepIn"];
const prevMinKeepout = "layout(400um)";
/** Migrate the old keepout on layout/nets to boardInsetValue by finding the safest (max) value */
export function getBestBoardInsetValueForLayout(layoutNodeUid, pcbLayoutNodes, pcbLayoutRuleSets, documentElements, documentNets, documentData, logger) {
    const treeManager = new PcbTreeManager(pcbLayoutNodes, logger, true);
    const ruleCompiler = new PcbLayoutRuleCompiler(documentElements, documentNets, pcbLayoutRuleSets, treeManager.treeNodes, documentData, new PcbLayoutRuntimeRulesManager(), logger);
    const layoutNode = treeManager.treeNodes[layoutNodeUid];
    if (!layoutNode || layoutNode.type !== PcbNodeTypes.layout)
        return;
    const layoutRules = ruleCompiler.getFlattenedRules(layoutNode);
    const layoutRulesParsed = migrationParseRules(layoutNode, layoutRules, ["keepOut", "minKeepOut"], pcbLayoutNodes, documentElements, documentData, logger);
    const bestLayoutKeepout = calcKeepoutForNodeAndType(layoutNode, layoutRulesParsed, PcbNodeTypes.net);
    const netNodesBakedWithFills = Object.values(treeManager.treeNodes)
        .filter((n) => n.type === PcbNodeTypes.net)
        .map((net) => {
        const flattened = ruleCompiler.getFlattenedRules(net);
        const minKeepoutRule = flattened["minKeepOut"];
        if (minKeepoutRule && minKeepoutRule.uid === "root-minKeepOut-rule") {
            // The previous system default is 400um
            minKeepoutRule.value = prevMinKeepout;
        }
        const parsed = migrationParseRules(net, flattened, ["keepOut", "minKeepOut", "connectedLayers"], pcbLayoutNodes, documentElements, documentData, logger);
        return { net, flattened, parsed };
    })
        .filter((netInfo) => {
        return netInfo.parsed.connectedLayers && netInfo.parsed.connectedLayers.length > 0;
    });
    const bestNetKeepout = Math.max(...netNodesBakedWithFills.map((netInfo) => calcKeepoutForNodeAndType(netInfo.net, netInfo.parsed, PcbNodeTypes.layout)));
    const bestKeepout = Math.max(bestLayoutKeepout, bestNetKeepout);
    return bestKeepout;
}
// Collect the best value for the Board Inset Margin and set it to the root layout
export function keepExistingBoardEdgeSpacing(pcbLayoutNodes, pcbLayoutRuleSets, documentElements, documentNets, documentData, logger) {
    const nodesArray = Object.values(pcbLayoutNodes);
    const newNodes = { ...pcbLayoutNodes };
    const layoutNodes = nodesArray.filter((n) => n.type === PcbNodeTypes.layout);
    // eslint-disable-next-line @buildwithflux/no-array-foreach
    layoutNodes.forEach((layoutNode) => {
        if (layoutNode.pcbNodeRuleSet?.boardInsetMargin) {
            // Already has new rule, skip migration
            return;
        }
        const layoutKeepout = getBestBoardInsetValueForLayout(layoutNode.uid, pcbLayoutNodes, pcbLayoutRuleSets, documentElements, documentNets, documentData, logger);
        if (layoutKeepout !== undefined) {
            const expected = getExpectedNewRuleValue(layoutNode, "boardInsetMargin", pcbLayoutNodes, pcbLayoutRuleSets, documentElements, documentNets, documentData, logger);
            // Avoid setting it if not needed
            if (expected !== layoutKeepout) {
                newNodes[layoutNode.uid] = {
                    ...layoutNode,
                    pcbNodeRuleSet: {
                        ...(layoutNode.pcbNodeRuleSet ?? {}),
                        boardInsetMargin: {
                            uid: "boardInsetMargin",
                            key: "boardInsetMargin",
                            value: humanizeLengthValue(layoutKeepout, "mm"),
                            disabled: false,
                        },
                    },
                };
            }
        }
    });
    // Wipe all keepout local rules on layouts
    return wipeLocalRulesFromNodeType(PcbNodeTypes.layout, keepoutRuleKeys, newNodes);
    // We don't care about removing rulesets targeting layouts too much, since they don't mean anything anymore there
}
//# sourceMappingURL=boardInsetLayoutMigration.js.map